import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import {
  getFlyoutMicrosoftOnboardingFormData,
  getFlyOutOnboardingData,
} from './flyoutOnboardingActions'
import { isLoading } from '../../utils/loadingState'
import { LoadingTypes } from '../../modules/loading/reducer'
import { PartnerType } from '../../types/partner'
import { useOnboardingTenantPartnerId } from '../../utils/hooks/useCrmOnboardingContext'
import { useUserType } from '../../utils/hooks/useUserType'
import type { CloudSettingsState } from './cloudSettingsState'
import {
  getFlyoutListingData,
  getFlyoutListingDataMicrosoft,
} from './sellerListingActions'

const useFlyoutAccountId = ({
  product,
  cloud,
}: {
  product: string
  cloud: string
}) => {
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()

  return useSelector((state: CloudSettingsState) => {
    if (accountIdFromParams) {
      return accountIdFromParams
    }

    const accounts = state.accountSelection?.availableAccounts
    const accountData = (Array.isArray(accounts) ? accounts : []).filter(
      account => account?.product?.toLowerCase() === product
    )
    return accountData?.find(account => account?.cloud === cloud)?.id || ''
  })
}

export const useSellerInfo = () => {
  const tenantPartnerId = useOnboardingTenantPartnerId()
  const { pathname } = useLocation()
  const isAdmin = useUserType() === PartnerType.Admin
  const {
    partnerData,
    flyoutSellerInfo,
    isLoadingSeller,
    hasFlyoutOnboardingData,
    microsoftSellerInfo,
    hasMicrosoftOnboardingData,
  } = useSelector((state: CloudSettingsState) => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
    partnerData: state.AccountData.user.partnerData || undefined,
    isLoadingSeller:
      isLoading(state.loading)(LoadingTypes.MARKETPLACE_INFO) ||
      isLoading(state.loading)(LoadingTypes.FLYOUT_ONBOARDING_DATA) ||
      isLoading(state.loading)(LoadingTypes.MARKETPLACE_INFO_MICROSOFT),
    hasFlyoutOnboardingData: !isEmpty(state.flyOutOnboarding.onboardingForm),
    microsoftSellerInfo: state.flyOutSellerInfoMicrosoft,
    hasMicrosoftOnboardingData:
      state.flyOutOnboarding.microsoftOnboardingForm.submit,
  }))
  const flyoutProduct = partnerData?.subscribedProducts?.FLYOUT
  const flyoutSubscribedCloudProviders = flyoutProduct?.cloudProviders || []
  const dispatch = useDispatch() as (action: unknown) => Promise<unknown>

  const accountIdAWS = useFlyoutAccountId({ product: 'flyout', cloud: 'AWS' })
  const accountIdMicrosoft = useFlyoutAccountId({
    product: 'flyout',
    cloud: 'AZURE',
  })

  const isOnboardingRoute = pathname.includes('/onboarding')
  const isAWSSettingsRoute =
    pathname.includes('/settings/cloud/aws') ||
    (pathname.includes('/cloud/aws') && !isOnboardingRoute)

  useEffect(() => {
    if (
      accountIdAWS &&
      partnerData?.activeProducts &&
      partnerData?.activeProducts?.includes('flyout') &&
      flyoutSubscribedCloudProviders?.includes('AWS')
    ) {
      dispatch(
        getFlyoutListingData({
          partnerId: accountIdAWS,
          metadata: true,
        })
      )
    }
  }, [
    partnerData?.activeProducts,
    flyoutSubscribedCloudProviders,
    accountIdAWS,
  ])
  useEffect(() => {
    if (
      accountIdMicrosoft &&
      partnerData?.activeProducts &&
      partnerData?.activeProducts?.includes('flyout') &&
      partnerData?.activeProductsWithCloud?.includes('FLYOUT_AZURE') &&
      flyoutSubscribedCloudProviders?.includes('AZURE') &&
      !isAWSSettingsRoute
    ) {
      dispatch(
        getFlyoutListingDataMicrosoft({
          accountId: accountIdMicrosoft,
        })
      )
    }
  }, [
    partnerData?.partnerId,
    partnerData?.activeProducts,
    flyoutSubscribedCloudProviders,
    accountIdAWS,
    accountIdMicrosoft,
  ])
  const loading = Boolean(!partnerData || isLoadingSeller)

  const flyoutOnboardingFetchAttemptedRef = useRef(false)

  useEffect(() => {
    flyoutOnboardingFetchAttemptedRef.current = false
  }, [accountIdAWS, tenantPartnerId])

  useEffect(() => {
    if (
      accountIdAWS &&
      tenantPartnerId &&
      !flyoutSellerInfo?.onboarded &&
      !loading &&
      !hasFlyoutOnboardingData &&
      flyoutSubscribedCloudProviders?.includes('AWS') &&
      !isAWSSettingsRoute &&
      !isAdmin &&
      !flyoutOnboardingFetchAttemptedRef.current
    ) {
      flyoutOnboardingFetchAttemptedRef.current = true
      dispatch(
        getFlyOutOnboardingData(
          accountIdAWS,
          tenantPartnerId,
          'AWS' as never
        ) as never
      )
    }
  }, [
    accountIdAWS,
    tenantPartnerId,
    flyoutSellerInfo,
    loading,
    hasFlyoutOnboardingData,
    flyoutSubscribedCloudProviders,
    isAWSSettingsRoute,
    isAdmin,
  ])
  useEffect(() => {
    if (
      accountIdMicrosoft &&
      !microsoftSellerInfo?.onboarded &&
      !loading &&
      !hasMicrosoftOnboardingData &&
      partnerData?.activeProductsWithCloud?.includes('FLYOUT_AZURE') &&
      flyoutSubscribedCloudProviders?.includes('AZURE')
    ) {
      dispatch(
        getFlyoutMicrosoftOnboardingFormData(
          accountIdMicrosoft,
          'AZURE' as never,
          false
        ) as never
      )
    }
  }, [
    accountIdMicrosoft,
    microsoftSellerInfo,
    loading,
    flyoutSubscribedCloudProviders,
    hasMicrosoftOnboardingData,
  ])
  return { loading: loading, flyoutSellerInfo, microsoftSellerInfo }
}
